// What a broker governance dialog says after the operator confirmed
// (SM-716 AC): the audit reference of a success, or the server's refusal
// verbatim — with a BROKER_CLOSE_BLOCKED's obligations laid out one per
// row (currency, amount, what clears it) — or, when broker-service never
// answered, that the outcome is unknown. That last case never offers a
// second submit: the operator reloads and reads the node's status and
// audit trail first.
import { Badge } from "@/components/ui/Badge";
import { CopyButton } from "@/components/ui/CopyButton";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import type { BlockingObligation } from "@/lib/broker-types";

import {
  blockingObligations,
  brokerErrorDetail,
  clearingActionLabel,
  obligationKindLabel,
  outcomeUnknown,
} from "./governance";
import { refusalText } from "./payouts";
import { canonToWire } from "./receivables";

/**
 * The audit events broker-service wrote for the action just confirmed.
 * Nothing at all when the response named none (an upstream that does
 * not stamp X-Broker-Audit-Event-Ids yet): the audit trail still holds
 * the row, and a blank is better than a claim either way.
 */
export function AuditReference({ ids }: { ids: string[] }) {
  if (ids.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">Audit reference</p>
      <ul className="space-y-1">
        {ids.map((id) => (
          <li key={id} className="flex items-center gap-2">
            <span className="font-mono text-2xs text-t-1">{id}</span>
            <CopyButton value={id} label="audit event id" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A refusal or failure, as the dialog renders it. */
export function RefusalNotice({
  error,
  unknown = outcomeUnknown(error),
  unknownNote,
  children,
}: {
  error: unknown;
  /** Whether the outcome is unknown; the dialogs with their own lock rule (fees, SM-839) decide it. */
  unknown?: boolean;
  /** Replaces the default "outcome is not confirmed" advice. */
  unknownNote?: string;
  /** What the refusal means, under the server's code and message. */
  children?: React.ReactNode;
}) {
  if (!error) return null;
  const blocking = blockingObligations(error);
  const requestID = brokerErrorDetail(error)?.request_id;
  return (
    <div className="space-y-2">
      {/* An unconfirmed outcome (5xx, network) carries no refusal worth
          reading: its code and message are for the tech team (SM-956). */}
      {unknown ? null : <p className="text-xs text-danger">{refusalText(error)}</p>}
      {children}
      {blocking.length > 0 ? <BlockingObligationList items={blocking} /> : null}
      {unknown ? (
        <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-2xs text-warning">
          {unknownNote ??
            "The outcome is not confirmed: the request did not get a definite answer. Close this dialog, reload the broker and check its status and audit trail before doing anything else."}
        </p>
      ) : null}
      {requestID ? (
        <p className="flex gap-2 text-2xs text-muted">
          <span>Request ID</span>
          <span className="font-mono">{requestID}</span>
        </p>
      ) : null}
      {unknown ? <TechnicalText error={error} /> : null}
    </div>
  );
}

/** The server's code and message, collapsed, for the tech team. */
export function TechnicalText({ error }: { error: unknown }) {
  const text = refusalText(error);
  if (!text) return null;
  return (
    <details className="text-2xs text-muted">
      <summary className="cursor-pointer">Technical details</summary>
      <p className="mt-1 break-words font-mono">{text}</p>
    </details>
  );
}

/** A read that failed, in plain words; the server's code and message under Technical details. */
export function LoadProblem({ error, message }: { error: unknown; message: string }) {
  return (
    <div className="space-y-1" role="alert">
      <p className="text-xs text-danger">{message}</p>
      <TechnicalText error={error} />
    </div>
  );
}

/** One row per obligation standing between the node and CLOSED. Never summed. */
export function BlockingObligationList({ items }: { items: BlockingObligation[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-2xs">
        <thead className="bg-surface-2">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium uppercase tracking-wide text-muted">Obligation</th>
            <th className="px-2 py-1.5 text-right font-medium uppercase tracking-wide text-muted">Amount</th>
            <th className="px-2 py-1.5 text-left font-medium uppercase tracking-wide text-muted">What clears it</th>
          </tr>
        </thead>
        <tbody>
          {items.map((o, i) => (
            <tr key={`${o.kind}-${o.currency ?? ""}-${i}`} className="border-t border-border/60 align-top">
              <td className="px-2 py-1.5">
                <div className="font-medium text-t-1">{obligationKindLabel(o.kind)}</div>
                {o.subject_ids && o.subject_ids.length > 0 ? (
                  <div className="mt-0.5 break-all font-mono text-muted">{o.subject_ids.join(", ")}</div>
                ) : null}
              </td>
              <td className="px-2 py-1.5 text-right">
                <ObligationFigure o={o} />
              </td>
              <td className="px-2 py-1.5 text-t-2">{clearingActionLabel(o.clearing_action)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObligationFigure({ o }: { o: BlockingObligation }) {
  if (o.currency && o.amount) {
    const wire = canonToWire(o.amount);
    return wire !== undefined ? (
      <CurrencyAmount currency={o.currency} value={wire} />
    ) : (
      <span className="font-mono">
        {o.amount} <Badge tone="muted">{o.currency}</Badge>
      </span>
    );
  }
  if (o.count) return <span className="font-mono">{o.count}</span>;
  return <span className="text-muted">—</span>;
}
